import type { Challenge } from '@source/graphql/autogenerate/schemas';
import { Dispatch, SetStateAction } from 'react';
import { Modal } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from 'styled-components';
import { IDefaultTheme } from '@source/styles/themes';
import CompanyWhatsappButton from '../CompanyWhatsappButton';
import Image from 'next/legacy/image';
import getPixelRatio from '@source/utils/getPixelRatio';
import * as S from './styles';
import { unMask } from '@source/utils/masks';

type CompanyWhatsappModalProps = {
  deviceWidth: number;
  isModalOpen: boolean;
  setIsModalOpen: Dispatch<SetStateAction<boolean>>;
  challengeDetails: Challenge;
  customButtonStyle?: React.CSSProperties;
};

const CompanyWhatsappModal: React.FC<CompanyWhatsappModalProps> = ({
  deviceWidth,
  isModalOpen,
  setIsModalOpen,
  challengeDetails,
  customButtonStyle
}): JSX.Element => {
  const theme = useTheme() as IDefaultTheme;

  const companyPhone = unMask(
    (challengeDetails?.company?.service_whatsapp_number || '').replace('+', '')
  );

  const encodedGreetingText = encodeURIComponent(
    `Oi, tenho uma dúvida sobre o ${challengeDetails.name}`
  );

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const getCompanyText = () => {
    const serviceInformation =
      challengeDetails?.company?.company_settings?.service_information_whatsapp;

    return (
      serviceInformation ||
      'Olá tudo bem!?\nQuer falar com a gente? Fale pelo WhatsApp!'
    );
  };

  return (
    <Modal
      open={isModalOpen}
      onClose={handleCloseModal}
      BackdropProps={{
        style: { backgroundColor: theme.colors.modalBackground }
      }}
      sx={{
        display: 'grid',
        placeItems: 'center',
        overflowY: 'scroll',
        bottom: { xs: '0' }
      }}
    >
      <S.ModalWrapper
        style={{
          background: `url('${process.env.NEXT_PUBLIC_CDN_URL}/assets/general/whatsapp-background.jpg')`
        }}
      >
        <S.TopContainer>
          <Image
            src={`/${challengeDetails?.image_avatar}?wdt=${getPixelRatio(
              64
            )}&hgt=${getPixelRatio(64)}`}
            loader={({ src }) => `${process.env.NEXT_PUBLIC_CDN_URL}${src}`}
            width={64}
            height={64}
            alt={`${challengeDetails?.company?.fantasy_name} avatar`}
          />
          <S.CompanyInfo>
            <p>{challengeDetails?.company?.fantasy_name}</p>
            <p>Atendimento</p>
          </S.CompanyInfo>
          <S.CloseModalButton onClick={handleCloseModal}>
            <CloseIcon sx={{ fontSize: '2.4rem', color: theme.colors.white }} />
          </S.CloseModalButton>
        </S.TopContainer>

        <S.Content>
          <S.ServiceMessage>{getCompanyText()}</S.ServiceMessage>

          <S.WhatsappAnchor
            target="_blank"
            rel="noreferrer"
            href={`https://api.WhatsApp.com/send?phone=${companyPhone}&text=${encodedGreetingText}`}
          >
            Iniciar conversa
          </S.WhatsappAnchor>
        </S.Content>

        <CompanyWhatsappButton
          customButtonStyle={{
            ...customButtonStyle,
            ...(deviceWidth < 700 && { display: 'none' })
          }}
          customAction={handleCloseModal}
        />
      </S.ModalWrapper>
    </Modal>
  );
};

export default CompanyWhatsappModal;
